/**
 * PATH: packages/ui/src/components/settings/SecurityTab.tsx
 * PURPOSE: Orchestrator for security settings — resolves API endpoints and delegates
 *          rendering to TwoFactorSection, SessionsSection, and SecurityTips.
 *
 * WHY: Security controls must be available across hubs without leaving the
 *      universal settings modal.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌───────────────┐
 *   │ Resolve APIs │───▶│ Sub-sections │───▶│ User actions   │
 *   └──────────────┘    └──────────────┘    └───────────────┘
 *
 * DEPENDENCIES:
 *   - ./security-tab/TwoFactorSection: 2FA setup/disable/backup flow
 *   - ./security-tab/SessionsSection: active session management
 *   - ./security-tab/SecurityTips: static security tips
 *   - ./types (SettingsApiClient): optional API bridge for auth endpoints
 */

import { useCallback, useMemo } from 'react'
import type { SettingsApiClient } from './types'
import { TwoFactorSection } from './security-tab/TwoFactorSection'
import { SessionsSection } from './security-tab/SessionsSection'
import { SecurityTips } from './security-tab/SecurityTips'
import { MfaMethodsSection } from './security-tab/MfaMethodsSection'
import { MfaPolicySection } from './security-tab/MfaPolicySection'
import { TrustedDevicesSection } from './security-tab/TrustedDevicesSection'
import { InAppAuthenticatorSection } from './security-tab/InAppAuthenticatorSection'
import { AccountSwitcherSection, type AccountVault } from './security-tab/AccountSwitcherSection'

// Re-export types so existing consumers are not broken
export type { Session } from './security-tab/security-tab-types'

export interface SecurityTabProps {
  user?: {
    id: string
    email: string
    totp_enabled?: boolean
  }
  /** API client for default 2FA/session handlers */
  apiClient?: SettingsApiClient
  /**
   * Whether the current user can manage the organisation-wide MFA policy.
   * When true (and an apiClient is present) the admin policy section renders.
   * Defaults to false — non-admins never see org controls.
   */
  isAdmin?: boolean
  /**
   * Render the SMS/email MFA enrolment section. Defaults to true whenever an
   * apiClient is available so MFA is available across every hub's settings.
   * Hubs whose backend lacks the /auth/mfa/* routes can pass false to opt out.
   */
  showMfaMethods?: boolean
  /** Override default endpoints if needed */
  endpoints?: {
    setup?: string
    confirm?: string
    disable?: string
    backup?: string
    status?: string
    sessions?: string
    revokeSession?: string
    revokeAllSessions?: string
    // Unified MFA (SMS + email enrolment, status, org policy)
    mfaStatus?: string
    mfaSmsStart?: string
    mfaSmsConfirm?: string
    mfaEmailStart?: string
    mfaEmailConfirm?: string
    mfaDisable?: string
    mfaPolicy?: string
  }
  onEnable2FA?: () => Promise<{
    secret: string
    qr_code: string
    provisioning_uri: string
  }>
  onConfirm2FA?: (code: string) => Promise<{
    success: boolean
    backup_codes?: string[]
    error?: string
  }>
  onDisable2FA?: (code: string, password?: string) => Promise<{
    success: boolean
    error?: string
  }>
  onRegenerateBackupCodes?: (code: string) => Promise<{
    success: boolean
    backup_codes?: string[]
    error?: string
  }>
  onGet2FAStatus?: () => Promise<{
    enabled: boolean
    backup_codes_remaining: number
    last_used: string | null
  }>
  onGetSessions?: () => Promise<import('./security-tab/security-tab-types').Session[]>
  onRevokeSession?: (sessionId: string) => Promise<{ success: boolean }>
  onRevokeAllSessions?: () => Promise<{ success: boolean }>
  /**
   * Multi-account vault (the accountSwitcher functions from @quotech/auth).
   * Injected rather than imported so tenant SPAs that compile @quotech/ui
   * without @quotech/auth don't pull the dependency. Section hidden when
   * not provided.
   */
  accountVault?: AccountVault
  /** Login path for "Add another account" (defaults to /login). */
  loginPath?: string
}

export function SecurityTab({
  user,
  apiClient,
  endpoints,
  isAdmin = false,
  showMfaMethods = true,
  onEnable2FA,
  onConfirm2FA,
  onDisable2FA,
  onRegenerateBackupCodes,
  onGet2FAStatus,
  onGetSessions,
  onRevokeSession,
  onRevokeAllSessions,
  accountVault,
  loginPath,
}: SecurityTabProps) {
  // NOTE: apiClient baseURL already includes /api, so endpoints should NOT include /api prefix
  const resolved = useMemo(() => ({
    setup: endpoints?.setup || '/auth/2fa/setup/',
    confirm: endpoints?.confirm || '/auth/2fa/confirm/',
    disable: endpoints?.disable || '/auth/2fa/disable/',
    backup: endpoints?.backup || '/auth/2fa/backup/',
    status: endpoints?.status || '/auth/2fa/status/',
    sessions: endpoints?.sessions || '/auth/sessions/',
    revokeSession: endpoints?.revokeSession || '/auth/sessions/revoke/',
    revokeAllSessions: endpoints?.revokeAllSessions || '/auth/sessions/revoke-all/',
  }), [endpoints])

  const mfaEndpoints = useMemo(() => ({
    status: endpoints?.mfaStatus || '/auth/mfa/status/',
    smsStart: endpoints?.mfaSmsStart || '/auth/mfa/sms/start/',
    smsConfirm: endpoints?.mfaSmsConfirm || '/auth/mfa/sms/confirm/',
    emailStart: endpoints?.mfaEmailStart || '/auth/mfa/email/start/',
    emailConfirm: endpoints?.mfaEmailConfirm || '/auth/mfa/email/confirm/',
    disable: endpoints?.mfaDisable || '/auth/mfa/disable/',
  }), [endpoints])

  const mfaPolicyEndpoint = endpoints?.mfaPolicy || '/auth/mfa/policy/'

  /* ───── 2FA callbacks ───── */

  const get2FAStatus = useCallback(async () => {
    if (onGet2FAStatus) return onGet2FAStatus()
    if (!apiClient) return null
    const response = await apiClient.get(resolved.status)
    return response?.data ?? response
  }, [apiClient, onGet2FAStatus, resolved.status])

  const enable2FA = useCallback(async () => {
    if (onEnable2FA) return onEnable2FA()
    if (!apiClient) throw new Error('2FA setup is not available')
    const response = await apiClient.post(resolved.setup, {})
    return response?.data ?? response
  }, [apiClient, onEnable2FA, resolved.setup])

  const confirm2FA = useCallback(async (code: string) => {
    if (onConfirm2FA) return onConfirm2FA(code)
    if (!apiClient) throw new Error('2FA confirmation is not available')
    const response = await apiClient.post(resolved.confirm, { code })
    const data = response?.data ?? response
    return { success: !!data?.success, backup_codes: data?.backup_codes, error: data?.error }
  }, [apiClient, onConfirm2FA, resolved.confirm])

  const disable2FA = useCallback(async (code: string) => {
    if (onDisable2FA) return onDisable2FA(code)
    if (!apiClient) throw new Error('2FA disable is not available')
    const response = await apiClient.post(resolved.disable, { code })
    const data = response?.data ?? response
    return { success: !!data?.success, error: data?.error }
  }, [apiClient, onDisable2FA, resolved.disable])

  const regenerateBackupCodes = useCallback(async (code: string) => {
    if (onRegenerateBackupCodes) return onRegenerateBackupCodes(code)
    if (!apiClient) throw new Error('Backup code regeneration is not available')
    const response = await apiClient.post(resolved.backup, { code })
    const data = response?.data ?? response
    return { success: !!data?.success, backup_codes: data?.backup_codes, error: data?.error }
  }, [apiClient, onRegenerateBackupCodes, resolved.backup])

  /* ───── Session callbacks ───── */

  const fetchSessions = useCallback(async () => {
    if (onGetSessions) return onGetSessions()
    if (!apiClient) return []
    const response = await apiClient.get(resolved.sessions)
    return response?.data?.sessions ?? response?.data ?? response
  }, [apiClient, onGetSessions, resolved.sessions])

  const revokeSession = useCallback(async (sessionId: string) => {
    if (onRevokeSession) return onRevokeSession(sessionId)
    if (!apiClient) return { success: false }
    const response = await apiClient.post(resolved.revokeSession, { session_id: sessionId })
    return response?.data ?? response
  }, [apiClient, onRevokeSession, resolved.revokeSession])

  const revokeAllSessions = useCallback(async () => {
    if (onRevokeAllSessions) return onRevokeAllSessions()
    if (!apiClient) return { success: false }
    const response = await apiClient.post(resolved.revokeAllSessions, {})
    return response?.data ?? response
  }, [apiClient, onRevokeAllSessions, resolved.revokeAllSessions])

  return (
    <div className="space-y-8 max-w-2xl">
      <TwoFactorSection
        user={user}
        enable2FA={enable2FA}
        confirm2FA={confirm2FA}
        disable2FA={disable2FA}
        regenerateBackupCodes={regenerateBackupCodes}
        get2FAStatus={get2FAStatus}
      />
      {/* 2026-07 auth arc: optional built-in TOTP generator (per device) */}
      <InAppAuthenticatorSection userId={user?.id} />
      {apiClient && showMfaMethods && (
        <MfaMethodsSection apiClient={apiClient} endpoints={mfaEndpoints} />
      )}
      {apiClient && isAdmin && (
        <MfaPolicySection apiClient={apiClient} endpoint={mfaPolicyEndpoint} />
      )}
      <SessionsSection
        fetchSessions={fetchSessions}
        revokeSession={revokeSession}
        revokeAllSessions={revokeAllSessions}
      />
      {apiClient && <TrustedDevicesSection apiClient={apiClient} />}
      {/* 2026-07 auth arc: multi-account login (vault lives in @quotech/auth) */}
      {accountVault && (
        <AccountSwitcherSection vault={accountVault} user={user} loginPath={loginPath} />
      )}
      <SecurityTips />
    </div>
  )
}

export default SecurityTab
